/**
 * Recursive descent parser for simple math formulas with @variable references.
 * Grammar:
 *   expr    = term (('+' | '-') term)*
 *   term    = unary (('*' | '/') unary)*
 *   unary   = '-'? primary
 *   primary = NUMBER | '@' IDENTIFIER | '(' expr ')'
 */

export function extractFormulaVariables(formula: string): string[] {
  const matches = formula.match(/@([a-zA-Z_]\w*)/g)
  if (!matches) return []
  return [...new Set(matches.map((m) => m.slice(1)))]
}

export function evaluateFormula(
  formula: string,
  variableValues: Record<string, number | string | boolean>
): number {
  // Replace @varName with numeric values
  const resolved = formula.replace(/@([a-zA-Z_]\w*)/g, (_, name) => {
    const val = variableValues[name]
    const n = Number(val)
    return isNaN(n) ? '0' : String(n)
  })

  try {
    return parseExpr(resolved.trim())
  } catch {
    return 0
  }
}

// ---- Parser internals ----

let pos: number
let input: string

function parseExpr(src: string): number {
  pos = 0
  input = src
  const result = expr()
  return result
}

function peek(): string {
  skipWhitespace()
  return input[pos] || ''
}

function consume(ch: string): void {
  skipWhitespace()
  if (input[pos] === ch) {
    pos++
  }
}

function skipWhitespace(): void {
  while (pos < input.length && input[pos] === ' ') pos++
}

function expr(): number {
  let left = term()
  while (true) {
    const op = peek()
    if (op === '+') { pos++; left = left + term() }
    else if (op === '-') { pos++; left = left - term() }
    else break
  }
  return left
}

function term(): number {
  let left = unary()
  while (true) {
    const op = peek()
    if (op === '*') { pos++; left = left * unary() }
    else if (op === '/') { pos++; const r = unary(); left = r !== 0 ? left / r : 0 }
    else break
  }
  return left
}

function unary(): number {
  if (peek() === '-') {
    pos++
    return -primary()
  }
  return primary()
}

function primary(): number {
  skipWhitespace()

  if (input[pos] === '(') {
    pos++
    const val = expr()
    consume(')')
    return val
  }

  // Parse number (including decimals)
  const start = pos
  if (input[pos] === '-' || input[pos] === '+') pos++
  while (pos < input.length && (input[pos] >= '0' && input[pos] <= '9' || input[pos] === '.')) pos++

  if (pos === start) return 0

  const num = parseFloat(input.slice(start, pos))
  return isNaN(num) ? 0 : num
}
